import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import useRecaptcha from '../../hooks/useRecaptcha';
import './BirthdayPartyInquiry.css';

export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return false;
  }
  // Require email to end with ".com"
  return email.toLowerCase().endsWith('.com');
};

const BirthdayPartyInquiry = () => {
  const [email, setEmail] = useState('');
  const [selectedStore, setSelectedStore] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [message, setMessage] = useState('');
  const [emailError, setEmailError] = useState('');
  const [storeError, setStoreError] = useState('');
  const { executeRecaptcha, isRecaptchaLoaded } = useRecaptcha();

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);

    // Clear previous errors when user starts typing
    if (emailError) {
      setEmailError('');
    }
    if (submitStatus === 'error') {
      setSubmitStatus('');
      setMessage('');
    }

    // Real-time validation feedback
    if (newEmail && !validateEmail(newEmail)) {
      setEmailError('Email address must end with .com');
    } else {
      setEmailError('');
    }
  };

  const handleStoreChange = (e) => {
    const newStore = e.target.value;
    setSelectedStore(newStore);

    // Clear store error when user selects a store
    if (storeError) {
      setStoreError('');
    }
    if (submitStatus === 'error') {
      setSubmitStatus('');
      setMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous errors
    setEmailError('');
    setStoreError('');

    if (!email) {
      setSubmitStatus('error');
      setMessage('Please enter your email address.');
      setEmailError('Email address is required');
      return;
    }

    if (!validateEmail(email)) {
      setSubmitStatus('error');
      setMessage('Please enter a valid email address ending with .com');
      setEmailError('Email address must end with .com');
      return;
    }

    if (!selectedStore) {
      setSubmitStatus('error');
      setMessage('Please select a store location.');
      setStoreError('Store selection is required');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('');
    setMessage('');

    // One submit attempt: fetch a FRESH reCAPTCHA token (never reuse - a
    // reused/expired v3 token is rejected server-side as
    // timeout-or-duplicate) and call the function.
    const attemptSubmit = async () => {
      let recaptchaToken = null;
      if (isRecaptchaLoaded()) {
        try {
          recaptchaToken = await executeRecaptcha('birthday_party_inquiry');
        } catch (recaptchaError) {
          console.warn('reCAPTCHA token fetch failed:', recaptchaError);
          if (process.env.NODE_ENV === 'production') {
            throw new Error('Security verification failed. Please try again.');
          }
        }
      } else if (process.env.NODE_ENV === 'production') {
        throw new Error('Security verification not available. Please refresh the page and try again.');
      }
      const sendBirthdayPartyInquiry = httpsCallable(getFunctions(), 'sendBirthdayPartyInquiry');
      return sendBirthdayPartyInquiry({
        customerEmail: email,
        selectedStore: selectedStore,
        recaptchaToken: recaptchaToken
      });
    };

    try {
      let result;
      try {
        result = await attemptSubmit();
      } catch (firstError) {
        // reCAPTCHA v3 fails a fraction of legitimate submissions
        // (duplicate/expired token, transient score). One automatic retry
        // with a brand-new token clears the common transient case before
        // the customer ever sees an error (added 2026-09-04 after a real
        // 1-in-4 "Security verification failed" during testing).
        if (firstError.code === 'functions/permission-denied') {
          await new Promise((r) => setTimeout(r, 400));
          result = await attemptSubmit();
        } else {
          throw firstError;
        }
      }

      if (result.data.success) {
        setSubmitStatus('success');
        setMessage('Thanks for reaching out to Lucky Claww! We\'ve sent birthday party information to your email - if you don\'t see it soon, please check your spam or junk folder.');
        // Reset form
        setEmail('');
      } else {
        throw new Error('Failed to send inquiry');
      }
    } catch (error) {
      console.error('Error sending birthday party inquiry:', error);
      setSubmitStatus('error');

      // Handle specific error types
      if (error.message.includes('Security verification')) {
        setMessage(error.message);
      } else if (error.code === 'functions/invalid-argument') {
        setMessage('Please check your email address and try again.');
      } else if (error.code === 'functions/unavailable') {
        setMessage('Service temporarily unavailable. Please try again in a few minutes.');
      } else if (error.code === 'functions/unauthenticated') {
        setMessage('Authentication error. Please refresh the page and try again.');
      } else if (error.code === 'functions/permission-denied') {
        setMessage('Security verification failed. Please try again.');
      } else {
        setMessage('Sorry, there was an error sending your inquiry. Please try again or contact us directly at luckyclaww@gmail.com.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="birthday-parties" className="birthday-party-section">
      <div className="birthday-party-container">
        <div className="birthday-party-header">
          <h2 className="birthday-party-heading">
            <i className="fas fa-birthday-cake heading-icon-left"></i>
            <span>Birthday Parties</span>
            <i className="fas fa-party-horn heading-icon-right"></i>
            <i className="fas fa-balloon heading-icon-right"></i>
          </h2>
          <p className="birthday-party-description">
            Make your special day unforgettable at Lucky Claww! We offer birthday party packages
            with exclusive access and guaranteed prizes for the birthday celebrant and all guests.
          </p>
        </div>

        <div className="birthday-party-content">
          <div className="birthday-party-info">
            <h3>🎉 What's Included:</h3>
            <ul>
              <li>🎮 <strong>Exclusive arcade access</strong></li>
              <li>🎁 <strong>Guaranteed prizes</strong></li>
              <li>🎈 <strong>Bring your own birthday decorations</strong></li>
            </ul>


            <div className="birthday-party-highlights">
              <div className="highlight-item">
                <i className="fas fa-users"></i>
                <span>Perfect for groups of 8-40 people</span>
              </div>
              <div className="highlight-item">
                <i className="fas fa-clock"></i>
                <span>Multiple party packages available</span>
              </div>
              <div className="highlight-item">
                <i className="fas fa-calendar-alt"></i>
                <span>Weekend and weekday options</span>
              </div>
            </div>
          </div>

          <div className={`birthday-party-form-container ${isSubmitting ? 'submitting' : ''} ${submitStatus}`}>
            <form onSubmit={handleSubmit} className="birthday-party-form">
              <h3>Contact us for more details</h3>
              <p>Select your preferred store and enter your email to receive detailed information about our birthday party packages!</p>

              <div className="form-group">
                <label htmlFor="store">Select Store *</label>
                <select
                  id="store"
                  value={selectedStore}
                  onChange={handleStoreChange}
                  required
                  className={storeError ? 'form-select error' : 'form-select'}
                >
                  <option value="">Please select a store location</option>
                  <option value="bellevue">📍 Bellevue Store - 14028 Bel-Red Rd, Unit 207, Bellevue, WA 98007</option>
                  <option value="kent">📍 Kent Store - 222 Washington Ave S, Kent, WA 98032</option>
                </select>
                {storeError && (
                  <div className="field-error">
                    {storeError}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={handleEmailChange}
                  required
                  placeholder="your.email@example.com"
                  className={emailError ? 'error' : ''}
                />
                {emailError && (
                  <div className="field-error">
                    {emailError}
                  </div>
                )}
              </div>

              {submitStatus && (
                <div className={`form-message ${submitStatus}`}>
                  {message}
                </div>
              )}

              <button
                type="submit"
                className="birthday-party-submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane"></i>
                    Get Party Information
                  </>
                )}
              </button>

              <p className="form-note">
                We'll respond within 24 hours with detailed pricing and availability.
              </p>
              
              <div className="recaptcha-notice">
                <i className="fas fa-shield-alt"></i>
                <span>This form is protected by reCAPTCHA to prevent spam and abuse.</span>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BirthdayPartyInquiry;